import React, { useState, useEffect } from 'react';
import { getManuals, deleteManual, getPrinterModels, askQuestion, uploadManual } from '../services/api';
import '../styles/TechChat.css';

function TechChat({ user }) {
  const [manuals, setManuals] = useState([]);
  const [printerModels, setPrinterModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('all');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  
  // העלאת מסמך
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadPrinterModel, setUploadPrinterModel] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [manualsRes, modelsRes] = await Promise.all([
        getManuals(),
        getPrinterModels()
      ]);
      setManuals(manualsRes.data.manuals);
      setPrinterModels(modelsRes.data.models);
    } catch (error) {
      console.error('שגיאה בטעינת נתונים:', error);
    }
  };

  const handleAsk = async () => {
    if (!question.trim()) return;
    
    setLoading(true);
    setAnswer('');
    
    // הוספה להיסטוריה
    const newQuestion = { type: 'question', text: question };
    setChatHistory(prev => [...prev, newQuestion]);
    
    try {
      const response = await askQuestion(question, selectedModel);
      const newAnswer = { type: 'answer', text: response.data.answer };
      setChatHistory(prev => [...prev, newAnswer]);
      setQuestion('');
    } catch (error) {
      const errorMsg = { type: 'error', text: error.response?.data?.message || 'שגיאה בקבלת תשובה' };
      setChatHistory(prev => [...prev, errorMsg]);
    }
    
    setLoading(false);
  };

  const handleUpload = async () => {
    if (!uploadName || !uploadPrinterModel || !uploadFile) {
      alert('יש למלא את כל השדות');
      return;
    }

    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('name', uploadName);
    formData.append('printerModel', uploadPrinterModel);

    try {
      await uploadManual(formData);
      alert('המסמך הועלה בהצלחה!');
      setShowUploadModal(false);
      setUploadName('');
      setUploadPrinterModel('');
      setUploadFile(null);
      loadData();
    } catch (error) {
      alert(error.response?.data?.message || 'שגיאה בהעלאת המסמך');
    }
    
    setUploading(false);
  };

  const handleDeleteManual = async (id, name) => {
    if (!window.confirm(`האם למחוק את "${name}"?`)) return;
    
    try {
      await deleteManual(id);
      loadData();
    } catch (error) {
      alert('שגיאה במחיקת המסמך');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  return (
    <div className="tech-chat-page">
      <div className="chat-header">
        <h2>🔧 עזרה טכנית למדפסות</h2>
        <p>שאל שאלה על קודי שגיאה, תקלות ותחזוקה</p>
      </div>

      <div className="chat-container">
        {/* בחירת דגם */}
        <div className="model-selector">
          <label>בחר דגם מדפסת:</label>
          <select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>
            <option value="all">כל הדגמים</option>
            {printerModels.map(model => (
              <option key={model} value={model}>{model}</option>
            ))}
          </select>
        </div>

        {/* היסטוריית צ'אט */}
        <div className="chat-history">
          {chatHistory.length === 0 ? (
            <div className="chat-empty">
              <span>💬</span>
              <p>שאל שאלה על המדפסת שלך</p>
              <p className="hint">לדוגמה: "מה זה קוד שגיאה E05?" או "איך מחליפים טונר?"</p>
            </div>
          ) : (
            chatHistory.map((item, index) => (
              <div key={index} className={`chat-bubble ${item.type}`}>
                {item.type === 'question' && <span className="bubble-icon">👤</span>}
                {item.type === 'answer' && <span className="bubble-icon">🤖</span>}
                {item.type === 'error' && <span className="bubble-icon">⚠️</span>}
                <div className="bubble-text">{item.text}</div>
              </div>
            ))
          )}
          {loading && (
            <div className="chat-bubble answer loading">
              <span className="bubble-icon">🤖</span>
              <div className="bubble-text">מחפש תשובה...</div>
            </div>
          )}
        </div>

        {/* שדה שאלה */}
        <div className="chat-input-container">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="הקלד שאלה..."
            rows="2"
            disabled={loading}
          />
          <button onClick={handleAsk} disabled={loading || !question.trim()}>
            {loading ? '...' : 'שלח'}
          </button>
        </div>
      </div>

      {/* ניהול מסמכים */}
      <div className="manuals-section">
        <div className="manuals-header">
          <h3>📚 מסמכים טכניים ({manuals.length})</h3>
          <button className="btn-upload" onClick={() => setShowUploadModal(true)}>
            + העלה מסמך
          </button>
        </div>

        {manuals.length === 0 ? (
          <div className="no-manuals">
            <p>אין מסמכים. העלה Service Manual כדי להתחיל.</p>
          </div>
        ) : (
          <div className="manuals-list">
            {manuals.map(manual => (
              <div key={manual._id} className="manual-item">
                <div className="manual-info">
                  <span className="manual-icon">📄</span>
                  <div>
                    <strong>{manual.name}</strong>
                    <span className="manual-model">{manual.printerModel}</span>
                  </div>
                </div>
                <button 
                  className="btn-delete-manual"
                  onClick={() => handleDeleteManual(manual._id, manual.name)}
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* מודל העלאה */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>📤 העלאת מסמך טכני</h3>
            
            <div className="form-group">
              <label>שם המסמך</label>
              <input
                type="text"
                placeholder="לדוגמה: Service Manual HP LaserJet"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                className="input-field"
              />
            </div>

            <div className="form-group">
              <label>דגם מדפסת</label>
              <input
                type="text"
                placeholder="לדוגמה: HP LaserJet Pro M404"
                value={uploadPrinterModel}
                onChange={(e) => setUploadPrinterModel(e.target.value)}
                className="input-field"
              />
            </div>

            <div className="form-group">
              <label>קובץ PDF</label>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="input-file"
              />
            </div>

            <div className="modal-actions">
              <button 
                className="btn-primary" 
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? 'מעלה...' : 'העלה'}
              </button>
              <button 
                className="btn-secondary" 
                onClick={() => setShowUploadModal(false)}
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TechChat;